import { useEffect } from 'react';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { newRelic } from '~/utils/newRelic/newRelic';
import { safeParseWithLogging } from '~/utils/zodHelpers';

import { INVOICE_ADJUSTMENTS_CACHE_KEY } from '../constants/invoicePage.const';
import { ADJUSTMENTS_RESPONSE_SCHEMA } from '../schemata/invoice.schema';
import * as invoicesService from '../services/invoices.service';
import {
  type InvoiceAdjustment,
  type InvoiceAdjustmentsResponse,
  type InvoiceJsonAdjustment,
} from '../types/invoice.type';

export const useFetchInvoiceAdjustments = (
  invoiceId: string,
): UseQueryResult<InvoiceAdjustment[], AxiosError> => {
  const query = useQuery<InvoiceAdjustmentsResponse, AxiosError, InvoiceAdjustment[]>({
    queryFn: async () => {
      const response = await invoicesService.getInvoiceAdjustmentsOfInvoice(invoiceId);
      return safeParseWithLogging(response, ADJUSTMENTS_RESPONSE_SCHEMA);
    },
    queryKey: [invoiceId, INVOICE_ADJUSTMENTS_CACHE_KEY],
    select: (data: InvoiceAdjustmentsResponse) =>
      data.results.map((result: InvoiceJsonAdjustment) => ({
        amount: result.amount,
        amountCurrency: result.amount_currency,
        description: result.description,
      })),
  });

  useEffect(() => {
    if (query.error)
      newRelic.noticeError('Error while fetching invoice items', { error: query.error });
  }, [query.error]);

  return query;
};
